import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button as MuiButton,
  IconButton,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import { Delete as DeleteIcon, Download as DownloadIcon } from '@mui/icons-material';
import Commons from '../../Services/Common/Common';
import Global from '../../Services/Common/Global';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../Reducer/SnackbarReducer';
import { setFileFields, resetFileFields } from '../../Reducer/FileFieldReducer';
import FilePreview from '../FilePreview/FilePreview';
import { Dialog } from '../MUIs';
import dayjs from 'dayjs';

/**
 * 그리드 row 바인딩이 없는 단일 파일 업로드 모달.
 * - HEA_BASIC_01의 FileUploadDialog UI/UX를 재사용하되, RIS_CUSTOM의 "규정등록하기"처럼 row가 없는 케이스를 지원
 * @param {Object|null} [props.initialExistingFile] - 서버에 이미 있는 파일 (규정 수정 등). { fileName, saveName, fullFilePath }
 */
function FileUploadDialogNoGrid({
  open,
  onClose,
  SaveLogicPreview,
  DownloadLogic,
  onSave,
  accept = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.hwp',
  title = '파일 업로드',
  filePreviewHeight = '600px',
  onUploaded, // { fileName, saveName, previewFilePath }
  fileNameField = 'fileName',
  filePathField = 'filePath',
  initialExistingFile = null,
}) {
  const dispatch = useDispatch();
  const fileUploadInputRef = useRef(null);

  const [selectedFileName, setSelectedFileName] = useState('');
  const [selectedSaveName, setSelectedSaveName] = useState('');
  const [previewFilePath, setPreviewFilePath] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    dispatch(resetFileFields());
    const ex = initialExistingFile;
    if (ex?.fullFilePath && (ex?.fileName || ex?.saveName)) {
      const normPath = String(ex.fullFilePath).replace(/\\/g, '/');
      setSelectedFileName(ex.fileName || ex.saveName || '');
      setSelectedSaveName(ex.saveName || '');
      setPreviewFilePath(normPath);
    } else {
      setSelectedFileName('');
      setSelectedSaveName('');
      setPreviewFilePath('');
    }
  }, [open, initialExistingFile, dispatch]);

  const handleBuiltinDownload = async () => {
    try {
      let filePath = '';
      let fileName = selectedFileName || 'downloaded_file';

      if (!previewFilePath) {
        dispatch(
          showSnackbar({
            message: '다운로드할 파일 경로가 없습니다.',
            type: 'warning',
          })
        );
        return;
      }

      filePath = previewFilePath.replace(
        `${Global.file.useUrl}files/`,
        ''
      ).replace(/^\/?files\//, '');

      const finalFileName =
        fileName === 'downloaded_file'
          ? decodeURIComponent(filePath.split('/').pop() || fileName)
          : fileName;

      const result = await Commons.Axios(
        'fileDown',
        Global.file.useUrl + Global.urls.Execute.UrlFileDownload,
        { FileName: finalFileName, Url: Commons.EncodeUrl(filePath) }
      );

      if (!result?.data || !result?.headers?.['content-type']) {
        dispatch(
          showSnackbar({
            message: `"${finalFileName}" 다운로드 응답이 비정상입니다.`,
            type: 'error',
          })
        );
        return;
      }

      const blob = new Blob([result.data], {
        type: result.headers['content-type'],
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = finalFileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      dispatch(
        showSnackbar({
          message: `"${finalFileName}" 다운로드 완료`,
          type: 'success',
        })
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error?.message || '다운로드 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };

  const handleFileClick = () => {
    fileUploadInputRef.current?.click();
  };

  const handleFileDelete = () => {
    dispatch(resetFileFields());
    setSelectedFileName('');
    setSelectedSaveName('');
    setPreviewFilePath('');
  };

  /**
   * SaveLogicPreview가 반환하는 previewPath가
   * - 'PreviewFiles/...' (정상)
   * - 'https://.../files/PreviewFiles/...' (URL 형태)
   * - 인코딩 문자열 등
   * 인 경우가 있어, moveAndReplaceFile이 기대하는 'PreviewFiles/...' 형태로 정규화합니다.
   */
  const normalizePreviewFilePath = (raw) => {
    if (!raw || typeof raw !== 'string') return '';

    const s = raw.replace(/\\/g, '/');

    // 1) PreviewFiles를 포함하면 그 구간부터 추출
    const idx = s.indexOf('PreviewFiles/');
    if (idx !== -1) return s.substring(idx);

    const idx2 = s.indexOf('PreviewFiles');
    if (idx2 !== -1) {
      return s
        .substring(idx2)
        .replace(/^PreviewFiles$/, 'PreviewFiles/');
    }

    // 2) URL 형태면 /files/ 다음을 디코딩해서 경로로 사용
    const marker = '/files/';
    const urlIdx = s.indexOf(marker);
    if (urlIdx !== -1) {
      const tail = s.substring(urlIdx + marker.length);
      try {
        return decodeURIComponent(tail);
      } catch {
        return tail;
      }
    }

    return raw;
  };

  const updateReduxPreview = ({ nextPreviewFileName, nextPreviewUrl }) => {
    dispatch(
      setFileFields({
        previewFileName: nextPreviewFileName,
        previewUrl: nextPreviewUrl,
      })
    );
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!SaveLogicPreview) {
      dispatch(
        showSnackbar({ message: '파일 업로드 로직이 연결되지 않았습니다.', type: 'warning' })
      );
      return;
    }

    if (file.name.length > 100) {
      dispatch(
        showSnackbar({
          message: '파일명 길이가 100자를 초과합니다. 100자 이하로 파일명을 수정해 주세요.',
          type: 'warning',
        })
      );
      return;
    }

    try {
      const timestamp = dayjs().format('YYYYMMDDHHmmss');
      const saveName = `${timestamp}_${file.name}`;

      // useFileUpload.SaveLogicPreview는 파일명(saveName)을 기반으로 PreviewFiles에 저장/변환하므로 File 객체에 saveName을 반영
      const fileToSave = new File([file], saveName, {
        type: file.type || 'application/octet-stream',
      });

      const previewPath = await SaveLogicPreview(fileToSave);
      if (!previewPath) return;

      const normalizedPreviewFilePath =
        normalizePreviewFilePath(previewPath) || '';
      if (!normalizedPreviewFilePath) return;

      // 미리보기 URL 생성 (FilePreview는 redux previewUrl을 우선 사용)
      const ext = file.name.split('.').pop()?.toLowerCase();
      let newPreviewUrl = '';

      if (['png', 'jpg', 'jpeg'].includes(ext)) {
        // 이미지: data URL로 미리보기
        await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (event) => {
            newPreviewUrl = event.target?.result || '';
            resolve();
          };
          reader.onerror = () => resolve();
          reader.readAsDataURL(file);
        });
      } else if (ext === 'pdf' || ext === 'hwp' || ['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(ext)) {
        // PDF/문서: PreviewFiles 경로를 정식 URL로 변환
        if (ext === 'hwp' && String(previewPath).startsWith('http')) {
          newPreviewUrl = previewPath;
        } else {
          const fixedPath = Commons.EncodeUrl(normalizedPreviewFilePath);
          newPreviewUrl = fixedPath ? Global.file.useUrl + 'files/' + fixedPath : '';
        }
      } else {
        // 그 외: 동일하게 URL 변환
        const fixedPath = Commons.EncodeUrl(normalizedPreviewFilePath);
        newPreviewUrl = fixedPath ? Global.file.useUrl + 'files/' + fixedPath : '';
      }

      setSelectedFileName(file.name);
      setSelectedSaveName(saveName);
      setPreviewFilePath(normalizedPreviewFilePath);

      updateReduxPreview({
        nextPreviewFileName: file.name,
        nextPreviewUrl: newPreviewUrl,
      });

      onUploaded?.({
        fileName: file.name,
        saveName,
        previewFilePath: normalizedPreviewFilePath,
      });

      dispatch(
        showSnackbar({
          message: `파일이 업로드되었습니다: ${file.name}`,
          type: 'success',
        })
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `파일 업로드 실패: ${error?.message || '알 수 없는 오류'}`,
          type: 'error',
        })
      );
    } finally {
      if (fileUploadInputRef.current) fileUploadInputRef.current.value = '';
    }
  };

  return (
    <Dialog
      title={title}
      open={open}
      onClose={onClose}
      contentSx={{ display: 'flex', flexDirection: 'column' }}
      closeButtonName={'닫기'}
      maxWidth="xl"
      useSaveButton
      saveButtonName="저장"
      useSaveAfterClose={false}
      onSave={async () => {
        if (saving) return;
        const isNewPreviewUpload =
          typeof previewFilePath === 'string' &&
          previewFilePath.startsWith('PreviewFiles');
        if (
          !selectedFileName ||
          !selectedSaveName ||
          !previewFilePath ||
          !isNewPreviewUpload
        ) {
          dispatch(
            showSnackbar({
              message:
                '저장하려면 새 파일을 업로드하세요. (기존 파일만 보는 상태에서는 저장할 수 없습니다.)',
              type: 'warning',
            })
          );
          return;
        }

        setSaving(true);
        try {
          const ok = await onSave?.({
            fileName: selectedFileName,
            saveName: selectedSaveName,
            previewFilePath,
          });
          if (ok === false) return;
          onClose?.();
        } catch (e) {
          dispatch(
            showSnackbar({
              message: e?.message || '규정 저장에 실패했습니다.',
              type: 'error',
            })
          );
        } finally {
          setSaving(false);
        }
      }}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          padding: 2,
        },
      }}
      content={
        <Box sx={{ height: 'calc(100vh - 130px)', display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>
          {/* 상단 파일 선택 */}
          <Paper elevation={3} sx={{ p: 2, flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField
                placeholder="파일 선택"
                variant="outlined"
                size="small"
                sx={{ width: 550 }}
                value={selectedFileName || '파일 선택'}
                inputProps={{
                  readOnly: true,
                  tabIndex: -1,
                  style: { caretColor: 'transparent' },
                }}
                onClick={handleFileClick}
                InputLabelProps={{ shrink: true }}
              />

              <IconButton
                onClick={handleFileDelete}
                sx={{ ml: 1, color: 'red', backgroundColor: '#f0f0f0', borderRadius: '50%' }}
                disabled={!selectedFileName}
              >
                <DeleteIcon />
              </IconButton>

              <input
                type="file"
                ref={fileUploadInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept={accept}
              />

              {(DownloadLogic || (selectedFileName && previewFilePath)) && (
                <MuiButton
                  variant="contained"
                  color="success"
                  onClick={() =>
                    DownloadLogic ? DownloadLogic() : handleBuiltinDownload()
                  }
                >
                  <DownloadIcon fontSize="" />
                  파일 다운로드
                </MuiButton>
              )}
            </Stack>
          </Paper>

          {/* 미리보기 */}
          <Paper
            sx={{
              p: 2,
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <div />
              <Box />
            </Box>
            <FilePreview
              selectedRowData={[
                {
                  [fileNameField]: selectedFileName,
                  [filePathField]: previewFilePath,
                },
              ]}
              fileNameField={fileNameField}
              filePathField={filePathField}
              filePreviewHeight={filePreviewHeight}
            />
          </Paper>
        </Box>
      }
    />
  );
}

export default FileUploadDialogNoGrid;
